import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { debugWarn } from '@/lib/debugLogger';

/**
 * AuthGate — Global authentication barrier.
 * 
 * Blocks ALL child rendering until the auth subsystem has fully resolved:
 *   1. Session checked
 *   2. Role loaded (or confirmed absent)
 *   3. Cluster/hub data hydrated
 * 
 * Safety features:
 *   - Local 8s hard-cap prevents infinite loading even if AuthContext stalls
 *   - Visual indicator after 4s to reassure the user
 * 
 * This eliminates:
 *   - UI flicker during token refresh
 *   - Premature ProtectedRoute evaluations
 *   - Race conditions between session and role loading
 */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const { loading } = useAuth();
  const [forceReady, setForceReady] = useState(false);
  const [showSlowHint, setShowSlowHint] = useState(false);
  const timerRef = useRef<number | null>(null);
  const hintTimerRef = useRef<number | null>(null);

  useEffect(() => {
    console.log('[NetUno:AUTH_GATE]', {
      loading,
      forceReady,
      showSlowHint,
      route: `${window.location.pathname}${window.location.search}`,
      at: new Date().toISOString(),
    });
  }, [loading, forceReady, showSlowHint]);

  useEffect(() => {
    if (!loading) {
      // Auth resolved — clear timers
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (hintTimerRef.current) window.clearTimeout(hintTimerRef.current);
      setForceReady(false);
      setShowSlowHint(false);
      return;
    }

    // Show hint after 4s
    hintTimerRef.current = window.setTimeout(() => {
      setShowSlowHint(true);
    }, 4_000);

    // Hard-cap: force through after 8s
    timerRef.current = window.setTimeout(() => {
      debugWarn('auth', 'AuthGate hard-cap de 8s atingido — liberando renderização');
      setForceReady(true);
    }, 8_000);

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      if (hintTimerRef.current) window.clearTimeout(hintTimerRef.current);
    };
  }, [loading]);

  if (loading && !forceReady) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-3">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground animate-pulse">
          Carregando plataforma…
        </p>
        {showSlowHint && (
          <p className="text-xs text-muted-foreground/60 mt-2">
            Verificando sessão — aguarde um momento…
          </p>
        )}
      </div>
    );
  }

  return <>{children}</>;
}
